import { z } from 'zod'
import type { User, UserMetadata, UserIdentity } from '#/domain/user/User'
import type { UserPage, MyOrganizationsPage } from '#/domain/user/UserRepository'
import type { UserLog } from '#/domain/user/UserLog'

const ThemeSchema = z.enum(['Light', 'Dark']).nullish()
const LanguageSchema = z.enum(['en-US', 'es-ES']).nullish()

// Validates Theme and Language independently, so an invalid value in one doesn't drop a still-valid value in the other.
function sanitizeUserMetadata(raw: unknown): UserMetadata | undefined {
  if (typeof raw !== 'object' || raw === null) return undefined
  const candidate = raw as Record<string, unknown>
  const theme = ThemeSchema.safeParse(candidate.Theme)
  const language = LanguageSchema.safeParse(candidate.Language)
  return {
    Theme: (theme.success ? theme.data : undefined) ?? undefined,
    Language: (language.success ? language.data : undefined) ?? undefined,
  }
}

function resolveName(name: unknown, email: unknown, userId: string): string {
  const trimmedName = typeof name === 'string' ? name.trim() : ''
  if (trimmedName) return trimmedName
  const trimmedEmail = typeof email === 'string' ? email.trim() : ''
  if (trimmedEmail) return trimmedEmail
  return userId
}

function warnUnexpectedType(userId: string, field: string, value: unknown): void {
  console.warn(
    `buildUser: ${field} has an unexpected type for user ${userId} (received ${typeof value}); defaulting`,
  )
}

// Warns when the field is present but the wrong type, instead of silently defaulting.
function asStringField(value: unknown, userId: string, field: string): string | null {
  if (value === null || value === undefined) return null
  if (typeof value === 'string') return value
  warnUnexpectedType(userId, field, value)
  return null
}

// Excludes NaN, since typeof NaN === 'number' would otherwise let it through unnoticed.
function asNumberField(value: unknown, userId: string, field: string): number | null {
  if (value === null || value === undefined) return null
  if (typeof value === 'number' && !Number.isNaN(value)) return value
  warnUnexpectedType(userId, field, value)
  return null
}

function asIdentities(value: unknown, userId: string): UserIdentity[] {
  if (value === null || value === undefined) return []
  if (!Array.isArray(value)) {
    warnUnexpectedType(userId, 'Identities', value)
    return []
  }
  return value.filter(
    (item): item is UserIdentity =>
      typeof item === 'object' &&
      item !== null &&
      typeof (item as Record<string, unknown>).Connection === 'string' &&
      typeof (item as Record<string, unknown>).Provider === 'string',
  )
}

// UserId is the only field Zod validates strictly — it's the sole stable key a caller can act on
// (render, link to, use as a cache key). Every other field is `unknown` on purpose: Auth0 profiles
// routinely have missing or wrong-typed fields, and buildUser is the single place that recovers
// from that, so there's no second hand-rolled path to keep in sync with this schema.
export const UserDtoSchema = z
  .object({
    UserId: z.string().min(1),
    Name: z.unknown().optional(),
    Email: z.unknown().optional(),
    Picture: z.unknown().optional(),
    Identities: z.unknown().optional(),
    LoginsCount: z.unknown().optional(),
    LastLogin: z.unknown().optional(),
    CreatedAt: z.unknown().optional(),
    UserMetadata: z.unknown().optional(),
  })
  .transform((raw): User => buildUser(raw.UserId, raw))

export type UserResponseDto = z.input<typeof UserDtoSchema>

function buildUser(userId: string, candidate: Record<string, unknown>): User {
  return {
    UserId: userId,
    Name: resolveName(candidate.Name, candidate.Email, userId),
    Email: asStringField(candidate.Email, userId, 'Email')?.trim() || null,
    Picture: asStringField(candidate.Picture, userId, 'Picture') ?? '',
    Identities: asIdentities(candidate.Identities, userId),
    LoginsCount: asNumberField(candidate.LoginsCount, userId, 'LoginsCount') ?? 0,
    LastLogin: asStringField(candidate.LastLogin, userId, 'LastLogin') ?? '',
    CreatedAt: asStringField(candidate.CreatedAt, userId, 'CreatedAt') ?? '',
    UserMetadata: sanitizeUserMetadata(candidate.UserMetadata),
  }
}

// Only drops a row when UserId itself is missing/invalid — every other field is recovered by buildUser.
function toDomainOrNull(raw: unknown): User | null {
  const result = UserDtoSchema.safeParse(raw)
  return result.success ? result.data : null
}

export function toDomain(raw: UserResponseDto): User {
  return UserDtoSchema.parse(raw)
}

export function toDomainList(raw: UserResponseDto[]): User[] {
  return raw.map(toDomain)
}

const UserPageDtoSchema = z.object({
  Items: z.array(z.unknown()).nullish(),
  Total: z.number().nullish(),
})

export type UserPageResponseDto = z.input<typeof UserPageDtoSchema>

export function toDomainPage(raw: UserPageResponseDto): UserPage {
  const { Items, Total } = UserPageDtoSchema.parse(raw)
  const rawItems = Items ?? []
  const items = rawItems.map(toDomainOrNull).filter((u): u is User => u !== null)
  const dropped = rawItems.length - items.length
  if (dropped > 0) {
    console.warn(`toDomainPage: dropped ${dropped} unrecoverable user record(s)`)
  }
  // Total is collection-wide (used for pagination); dropped is only known for this page, so
  // subtracting it here would make the reported total fluctuate between fetches. Report it as-is.
  return { items, total: Total ?? 0 }
}

const UserLogDtoSchema = z
  .object({
    LogId: z.string().optional(),
    Date: z.string().optional(),
    Type: z.string().optional(),
    Description: z.string().optional(),
    ClientName: z.string().optional(),
    Ip: z.string().optional(),
    UserAgent: z.string().optional(),
  })
  .transform((raw): UserLog => ({
    LogId: raw.LogId,
    Date: raw.Date,
    Type: raw.Type,
    Description: raw.Description,
    ClientName: raw.ClientName,
    Ip: raw.Ip,
    UserAgent: raw.UserAgent,
  }))

export type UserLogResponseDto = z.input<typeof UserLogDtoSchema>

export function toDomainLogList(raw: UserLogResponseDto[]): UserLog[] {
  return raw.map((item) => UserLogDtoSchema.parse(item))
}

const MyOrganizationsPageDtoSchema = z.object({
  Total: z.number(),
  Items: z.array(z.object({ Id: z.string(), Name: z.string(), DisplayName: z.string() })),
})

export type MyOrganizationsPageResponseDto = z.input<typeof MyOrganizationsPageDtoSchema>

export function toDomainMyOrganizations(raw: MyOrganizationsPageResponseDto): MyOrganizationsPage {
  return MyOrganizationsPageDtoSchema.parse(raw)
}
